import { useState, useEffect, useCallback } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";

import api from "../../services/api";

/*
  Painel com a correção da IA, para o corretor revisar antes de liberar.

  A IA é ferramenta de apoio do corretor: ela sugere, ele decide. Nada aqui é
  gravado sozinho — a nota só entra quando ele clica em "Usar esta nota", e o
  comentário só chega ao aluno se ele marcar "Incluir para o aluno".

  Para o aluno, o que for publicado aparece como "Comentários adicionais", sem
  nenhuma indicação de origem em IA. Por isso o corretor pode editar o texto:
  ele está assinando aquilo.

  Só aparece em redações de atividades com modo_correcao = 'ia_humano'; nas demais
  o endpoint devolve 404 e o componente não renderiza nada.
*/

const COMPETENCIAS = [
  { chave: "c1", rotulo: "Competência 1", descricao: "Domínio da norma culta" },
  { chave: "c2", rotulo: "Competência 2", descricao: "Compreensão da proposta" },
  { chave: "c3", rotulo: "Competência 3", descricao: "Seleção e organização de argumentos" },
  { chave: "c4", rotulo: "Competência 4", descricao: "Mecanismos linguísticos" },
  { chave: "c5", rotulo: "Competência 5", descricao: "Proposta de intervenção" },
];

/*
  A IA devolve o comentário num bloco só, com as seções separadas por " | ":
  "Pontos fortes: a, b | Áreas de melhoria: c | Sugestões: d". Quebrar em linhas
  deixa o texto editável de forma legível; se o formato mudar, fica o texto cru.
*/
function formatarParaEdicao(texto) {
  if (!texto) return "";

  return String(texto)
    .split("|")
    .map((p) => p.trim())
    .filter(Boolean)
    .join("\n\n");
}

function PainelCorrecaoIA({ redacaoId, notas, onUsarNota, comentarios, onComentariosChange }) {
  const [correcao, setCorrecao] = useState(null);
  const [status, setStatus] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [reprocessando, setReprocessando] = useState(false);
  const [foraDoFluxo, setForaDoFluxo] = useState(false);
  const [expandida, setExpandida] = useState(null);

  const buscar = useCallback(async () => {
    try {
      const { data } = await api.get(`/correcao-ia-humano/${redacaoId}`);
      setCorrecao(data.correcao);
      setStatus(data.status);
      setForaDoFluxo(false);
    } catch (err) {
      if (err?.response?.status === 404) {
        setForaDoFluxo(true);
      } else {
        console.error("Erro ao buscar correção da IA:", err);
      }
    } finally {
      setCarregando(false);
    }
  }, [redacaoId]);

  useEffect(() => {
    setCarregando(true);
    buscar();
  }, [buscar]);

  /* Enquanto a IA processa, reconsulta sozinho — é o que faz a fila andar. */
  useEffect(() => {
    if (status !== "processando") return undefined;
    const timer = setInterval(buscar, 12000);
    return () => clearInterval(timer);
  }, [status, buscar]);

  const reprocessar = async () => {
    setReprocessando(true);
    try {
      await api.post(`/correcao-ia-humano/${redacaoId}/reprocessar`);
      toast.info("Correção solicitada à IA. O resultado aparece aqui em instantes.");
      await buscar();
    } catch (err) {
      toast.error(err?.response?.data?.erro || "Não foi possível solicitar a correção à IA.");
    } finally {
      setReprocessando(false);
    }
  };

  const alternarPublicacao = (chave, marcado) => {
    const proximo = { ...comentarios };

    if (marcado) {
      // começa com o texto da IA, mas o corretor pode reescrever antes de finalizar
      proximo[chave] = comentarios[chave] ?? formatarParaEdicao(correcao?.[`${chave}_text`]);
    } else {
      delete proximo[chave];
    }

    onComentariosChange(proximo);
  };

  if (foraDoFluxo) return null;

  if (carregando) {
    return (
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body text-center py-4">
          <Spinner animation="border" size="sm" className="mr-2" />
          <span className="text-muted">Carregando correção da IA...</span>
        </div>
      </div>
    );
  }

  const publicados = Object.keys(comentarios || {}).length;

  return (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-header bg-light border-0 d-flex align-items-center justify-content-between flex-wrap">
        <h5 className="mb-0 text-dark font-weight-bold">
          <i className="fas fa-robot text-primary mr-2"></i>
          Sugestão da IA
          {status === "concluida" && correcao?.nota_final !== null && (
            <span className="badge badge-primary ml-2" style={{ borderRadius: "12px" }}>
              {correcao.nota_final}/1000
            </span>
          )}
        </h5>

        <Button variant="outline-secondary" size="sm" onClick={reprocessar} disabled={reprocessando}>
          {reprocessando ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <>
              <i className="fas fa-sync-alt mr-1"></i>
              {status === "falhou" ? "Tentar novamente" : "Reprocessar"}
            </>
          )}
        </Button>
      </div>

      <div className="card-body">
        {status === "processando" && (
          <div className="text-muted">
            <Spinner animation="border" size="sm" className="mr-2" />
            A IA está corrigindo esta redação. Esta área atualiza sozinha.
          </div>
        )}

        {!correcao && status !== "processando" && (
          <div className="alert alert-secondary mb-0">
            <i className="fas fa-info-circle mr-2"></i>
            A IA ainda não corrigiu esta redação.{" "}
            Se o aluno já enviou, use <strong>Reprocessar</strong>.
          </div>
        )}

        {status === "falhou" && (
          <div className="alert alert-danger mb-0">
            <strong>
              <i className="fas fa-exclamation-triangle mr-2"></i>
              A IA não conseguiu corrigir.
            </strong>
            <div className="mt-1" style={{ fontSize: "13px" }}>{correcao?.erro}</div>
            <div className="mt-2" style={{ fontSize: "13px" }}>
              Corrija normalmente — a sugestão da IA é opcional.
            </div>
          </div>
        )}

        {status === "concluida" && (
          <>
            <div className="alert alert-warning" style={{ fontSize: "13px" }}>
              <i className="fas fa-exclamation-circle mr-2"></i>
              Nada aqui é gravado sozinho. A nota entra quando você clicar em{" "}
              <strong>Usar esta nota</strong>, e o comentário só chega ao aluno se você marcar{" "}
              <strong>Incluir para o aluno</strong> — onde aparece como{" "}
              <strong>Comentários adicionais</strong>, sem menção à IA.
            </div>

            {COMPETENCIAS.map((c) => {
              const notaIA = correcao[`${c.chave}_nota`];
              const comentarioIA = correcao[`${c.chave}_text`];
              const indice = Number(c.chave.replace("c", ""));
              const notaAtual = notas?.[indice - 1];
              const jaAplicada = notaIA !== null && Number(notaAtual) === Number(notaIA);
              const publicando = Object.prototype.hasOwnProperty.call(comentarios || {}, c.chave);
              const vendo = expandida === c.chave;

              return (
                <div key={c.chave} className="border rounded p-3 mb-2">
                  <div className="d-flex align-items-center justify-content-between flex-wrap mb-2">
                    <div style={{ minWidth: 0 }}>
                      <strong className="text-dark">{c.rotulo}</strong>
                      <div className="text-muted" style={{ fontSize: "12px" }}>{c.descricao}</div>
                    </div>

                    <div className="d-flex align-items-center">
                      <span className="badge badge-primary mr-2" style={{ borderRadius: "12px", fontSize: "13px" }}>
                        IA: {notaIA === null ? "—" : `${notaIA}/200`}
                      </span>

                      {notaIA !== null && (
                        <Button
                          variant={jaAplicada ? "success" : "outline-primary"}
                          size="sm"
                          onClick={() => onUsarNota(indice, Number(notaIA))}
                          disabled={jaAplicada}
                        >
                          <i className={`fas ${jaAplicada ? "fa-check" : "fa-magic"} mr-1`}></i>
                          {jaAplicada ? "Nota aplicada" : "Usar esta nota"}
                        </Button>
                      )}
                    </div>
                  </div>

                  {comentarioIA ? (
                    <>
                      <Form.Check
                        type="checkbox"
                        id={`publicar-${c.chave}`}
                        label="Incluir para o aluno"
                        checked={publicando}
                        onChange={(e) => alternarPublicacao(c.chave, e.target.checked)}
                      />

                      {publicando ? (
                        <>
                          <Form.Control
                            as="textarea"
                            rows={6}
                            className="mt-2"
                            style={{ fontSize: "13px" }}
                            value={comentarios[c.chave]}
                            onChange={(e) =>
                              onComentariosChange({ ...comentarios, [c.chave]: e.target.value })
                            }
                          />
                          <small className="form-text text-muted">
                            O aluno vai ler exatamente este texto. Edite à vontade.
                          </small>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 mt-1"
                            onClick={() => setExpandida(vendo ? null : c.chave)}
                          >
                            {vendo ? "Ocultar sugestão da IA" : "Ver sugestão da IA"}
                          </Button>

                          {vendo && (
                            <div
                              className="bg-light border rounded p-2 mt-1 text-muted"
                              style={{ fontSize: "13px", whiteSpace: "pre-wrap" }}
                            >
                              {formatarParaEdicao(comentarioIA)}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <small className="text-muted">A IA não gerou comentário para esta competência.</small>
                  )}
                </div>
              );
            })}

            <div className="text-muted" style={{ fontSize: "13px" }}>
              <i className="fas fa-user-check mr-1"></i>
              {publicados === 0
                ? "Nenhum comentário será enviado ao aluno."
                : `${publicados} comentário(s) serão enviados ao aluno como "Comentários adicionais".`}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PainelCorrecaoIA;
